import { IColor } from './scan-3d-elevation.models';

/**
 * Generates the elevation gradient color bands used for elevation rendering.
 * This creates a gradient from blue (below reference) to green
 * with black center (reference) and from green to red (above reference).
 *
 * @returns Array of tuples containing [percentage, color] pairs for the gradient
 */
export function getElevationGradient(bandCount: number): [number, IColor][] {
  // Generate below colors (blue to green gradient)
  const belowColors: IColor[] = [];
  for (let i = 0; i < bandCount; i++) {
    const progress = i / (bandCount - 1);
    const red = 0;
    const green = progress;
    const blue = 1 - progress;
    belowColors.push({ r: red, g: green, b: blue });
  }

  // Generate above colors (green to red gradient)
  const aboveColors: IColor[] = [];
  for (let i = 0; i < bandCount; i++) {
    const progress = i / (bandCount - 1);
    const red = progress;
    const green = 1 - progress;
    const blue = 0;
    aboveColors.push({ r: red, g: green, b: blue });
  }

  const black = { r: 0, g: 0, b: 0 };

  const centerStripWidth = 0.01;
  const center = 0.5;
  const centerStart = center - centerStripWidth / 2;
  const centerEnd = center + centerStripWidth / 2;

  const bandWidth = center / belowColors.length;

  const bands: [number, IColor][] = [];

  // Below colors (from 0 to centerStart)
  belowColors.forEach((color, i) => {
    const start = i * bandWidth;
    const end = i === belowColors.length - 1 ? centerStart : (i + 1) * bandWidth;
    bands.push([start, color], [end, color]);
  });

  // Center black strip
  bands.push([centerStart, black], [centerEnd, black]);

  // Above colors (from centerEnd to 1)
  aboveColors.forEach((color, i) => {
    const start = i === 0 ? centerEnd : center + i * bandWidth;
    const end = center + (i + 1) * bandWidth;
    bands.push([start, color], [Math.min(1, end), color]);
  });

  return bands;
}

/**
 * Converts an IColor object to a hex color string.
 *
 * @param color The color object with r, g, b values (0-1 range)
 * @returns Hex color string in format #rrggbb
 */
export function rgbToHex(color: IColor): string {
  const r = Math.round(color.r * 255);
  const g = Math.round(color.g * 255);
  const b = Math.round(color.b * 255);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}
